import { z } from 'zod';

import { reviewEventSchema, type ReviewEvent } from '../review-event.ts';
import { deckScopeSchema, deckSummarySchema } from '../deck.ts';
import { responseIdSchema } from './common.ts';
import { unlockRuleListSchema } from '../unlock-progression.ts';

export { deckSummarySchema } from '../deck.ts';

export const referenceDocumentSummarySchema = z
  .object({
    deckId: responseIdSchema,
    description: z.string(),
    expectedPageCount: z.number().int().positive(),
    hasLocalCopy: z.boolean(),
    id: responseIdSchema,
    title: z.string(),
  })
  .strict();

export const deckHistoryCardSchema = z
  .object({
    active: z.boolean(),
    id: responseIdSchema,
    topicId: responseIdSchema,
    unlocked: z.boolean().optional(),
  })
  .strict();
export const deckHistorySectionSchema = z
  .object({
    id: responseIdSchema,
    name: z.string(),
    slug: z.string(),
    unlocks: unlockRuleListSchema.optional(),
    unlocked: z.boolean().optional(),
  })
  .strict();
export const deckHistoryUnitSchema = z
  .object({
    id: responseIdSchema,
    name: z.string(),
    sectionId: responseIdSchema,
    slug: z.string(),
    unlocks: unlockRuleListSchema.optional(),
    unlocked: z.boolean().optional(),
  })
  .strict();
export const deckHistoryTopicSchema = z
  .object({
    id: responseIdSchema,
    name: z.string(),
    slug: z.string(),
    unitId: responseIdSchema,
    unlocks: unlockRuleListSchema.optional(),
    unlocked: z.boolean().optional(),
  })
  .strict();
export const deckHistorySchema = z
  .object({
    cards: z.array(deckHistoryCardSchema),
    cursor: z.string(),
    events: z.array(reviewEventSchema),
    sections: z.array(deckHistorySectionSchema),
    topics: z.array(deckHistoryTopicSchema),
    units: z.array(deckHistoryUnitSchema),
  })
  .strict();

const memberDeckSchema = z
  .object({
    cardCount: z.number().int().nonnegative(),
    deckId: responseIdSchema,
    documents: z.array(referenceDocumentSummarySchema),
    everCorrectCount: z.number().int().nonnegative(),
    history: deckHistorySchema,
    touchedCount: z.number().int().nonnegative(),
    lockedCardCount: z.number().int().nonnegative().optional(),
  })
  .strict();
const deckPageBaseSchema = z.object({
  description: z.string(),
  slug: z.string(),
  title: z.string(),
});
export const deckPageSchema = z.discriminatedUnion('isMember', [
  deckPageBaseSchema.extend({ isMember: z.literal(false) }).strict(),
  deckPageBaseSchema
    .extend({ isMember: z.literal(true), member: memberDeckSchema })
    .strict(),
]);

export const deckJoinSchema = z
  .object({ joined: z.literal(true), deckId: responseIdSchema })
  .strict();
export const deckUploadSchema = z
  .object({ uploaded: z.literal(true) })
  .strict();
export const deckPdfUploadSchema = z
  .object({
    uploaded: z.literal(true),
    pageCount: z.number().int().positive(),
    referenceDocumentId: responseIdSchema,
  })
  .strict();

export { deckScopeSchema };
export type { DeckScope } from '../deck.ts';

export type DeckSummary = z.infer<typeof deckSummarySchema>;
export type ReferenceDocumentSummary = z.infer<
  typeof referenceDocumentSummarySchema
>;
export type DeckHistory = z.infer<typeof deckHistorySchema>;
export type DeckHistoryCard = z.infer<typeof deckHistoryCardSchema>;
export type DeckHistorySection = z.infer<typeof deckHistorySectionSchema>;
export type DeckHistoryUnit = z.infer<typeof deckHistoryUnitSchema>;
export type DeckHistoryTopic = z.infer<typeof deckHistoryTopicSchema>;
export type DeckPage = z.infer<typeof deckPageSchema>;
export type DeckHistoryEvent = ReviewEvent;
export type DeckJoin = z.infer<typeof deckJoinSchema>;
